import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import cisceLogo    from '../../img/cisce.png';
import amblumLogo    from '../../img/india.png';
import digiLogo    from '../../img/digi.png';
import cisceFLogo   from '../../img/cisceF.png';
import chiefExecSig from '../../img/chief_exec_sig.png';
import chairmanSig  from '../../img/chairman_sig.png';
import { QRCodeSVG } from 'qrcode.react';

// ─────────────────────────────────────────────────────────────────────────────
//  UTILITIES
// ─────────────────────────────────────────────────────────────────────────────
const DIGIT_WORDS = ['ZERO','ONE','TWO','THREE','FOUR','FIVE','SIX','SEVEN','EIGHT','NINE'];
const digitWords = (n) => String(n).split('').map(d => isNaN(parseInt(d,10)) ? d : DIGIT_WORDS[parseInt(d,10)]).join(' ');
const fmtMarks   = (m) => (m === null || m === undefined) ? '' : `${m} ${digitWords(m)}`;
const fmtGrade   = (g) => {
  if (!g) return '';
  const x = String(g).trim();
  return /^\d$/.test(x) ? `${x} ${DIGIT_WORDS[parseInt(x, 10)]}` : x.toUpperCase();
};
const fmtDeclaration = (iso) => {
  if (!iso) return null;
  try { const d = new Date(iso); return [String(d.getDate()).padStart(2,'0'),String(d.getMonth()+1).padStart(2,'0'),d.getFullYear()].join('.'); } catch { return null; }
};
const fmtDob = (iso) => {
  if (!iso) return 'N/A';
  try { const d = new Date(iso); return [String(d.getDate()).padStart(2,'0'),String(d.getMonth()+1).padStart(2,'0'),d.getFullYear()].join('-'); } catch { return 'N/A'; }
};
const fmtIndexNo = (s) => { if (!s) return 'N/A'; s = String(s); return s.length === 10 ? `${s.slice(0,7)}/${s.slice(7)}` : s; };
const extractParents = (sonOf) => {
  if (!sonOf) return { mother: 'N/A', father: 'N/A' };
  const lines = sonOf.split('\n');
  return {
    mother: (lines[0] || '').replace(/^Smt\s+/i, '').trim().toUpperCase() || 'N/A',
    father: (lines[1] || '').replace(/^Shri\s+/i, '').trim().toUpperCase() || 'N/A',
  };
};

// ─────────────────────────────────────────────────────────────────────────────
//  BASE
// ─────────────────────────────────────────────────────────────────────────────
const BASE = { fontFamily: '"Times New Roman", Times, serif', color: '#000' };

const PAGE2_EXAM_DESCRIPTION = (
  <>
    The Indian School Certificate (Class XII) Examination is taken after a two-year course of studies<br/>
    beyond the Indian Certificate of Secondary Education Examination (ICSE - Class X) or its<br/>
    equivalent.
  </>
);

// ─────────────────────────────────────────────────────────────────────────────
//  COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const DigiLockerResultPage = () => {
  const API_BASE_URL = 'https://peakybalvinder.pythonanywhere.com/api';
  // const API_BASE_URL = 'http://127.0.0.1:5005/api';

  const navigate = useNavigate();
  const location = useLocation();

  const [studentData, setStudentData] = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  // 🆕 Track whether user arrived via QR scan (URL has ?rollid=)
  const [isQrScanView, setIsQrScanView] = useState(false);

  useEffect(() => {
    let currentId = null;

    // Detect QR-scan entry: URL has ?rollid= param
    const params = new URLSearchParams(window.location.search);
    const urlRollId = params.get('rollid');

    // Priority order: location.state → localStorage → URL param
    if (location.state?.indexNo) {
      currentId = location.state.indexNo;
    } else {
      const lsId = localStorage.getItem('digiLockerRollId');
      if (lsId) currentId = lsId;
      else if (urlRollId?.trim()) currentId = urlRollId.trim();
    }

    // 🆕 Show simplified mobile-style view ONLY when arrived via QR scan
    setIsQrScanView(!!urlRollId?.trim() && !location.state?.indexNo);

    if (!currentId) {
      setError('No result found! Please check your Index Number and try again.');
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/public/students/${currentId}`, { headers: { 'Content-Type': 'application/json' } });
        if (!res.ok) { const err = await res.json(); throw new Error(res.status === 404 ? 'Result not found.' : err.message || `HTTP ${res.status}`); }
        setStudentData(await res.json());
      } catch (err) { setError(err.message || 'Failed to load student data.'); }
      finally { setLoading(false); }
    })();
  }, [location.state]);

  const downloadPdf = () => {
    const el = document.getElementById('result-content');
    if (!el || !studentData) return;
    html2pdf().set({
      margin: 0,
      filename: `DigiLocker_${studentData.candidateName.replace(/\s+/g,'_')}_${studentData.indexNo}.pdf`,
      image: { type:'jpeg', quality:0.98 },
      html2canvas: { scale:4, useCORS:true, logging:false },
      jsPDF: { unit:'mm', format:'a4', orientation:'portrait' },
    }).from(el).save();
  };

  // ── Loading / error / empty states ────────────────────────────────────
  const wrapperBg = { background:'#e5e7eb', minHeight:'100vh' };

  if (loading) return <div style={{ ...wrapperBg, display:'flex', justifyContent:'center', alignItems:'center' }}><p style={{ ...BASE, fontSize:'16px', color:'#374151' }}>Loading result…</p></div>;
  if (error)   return <div style={{ ...wrapperBg, display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', gap:'14px' }}><p style={{ ...BASE, fontSize:'16px', color:'#dc2626' }}>Error: {error}</p><button onClick={() => navigate('/admin/dashboard')} style={{ background:'#2563eb', color:'#fff', padding:'8px 22px', border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'14px' }}>Go to Dashboard</button></div>;
  if (!studentData) return <div style={{ ...wrapperBg, display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', gap:'14px' }}><p style={BASE}>No student data found.</p><button onClick={() => navigate('/admin/dashboard')} style={{ background:'#2563eb', color:'#fff', padding:'8px 22px', border:'none', borderRadius:'7px', cursor:'pointer' }}>Go to Dashboard</button></div>;

  // ════════════════════════════════════════════════════════════════════════
  //  🆕 QR-SCAN VIEW — simplified mobile-style summary
  //  Shown ONLY when the page is opened via QR scan (URL ?rollid=...)
  // ════════════════════════════════════════════════════════════════════════
  if (isQrScanView) {
    const isXII_ = studentData.classSelected === 'Class XII';
    const examTitle = isXII_
      ? `INDIAN SCHOOL CERTIFICATE (CLASS XII) EXAMINATION ${studentData.resultYear}`
      : `INDIAN SCHOOL CERTIFICATE (CLASS X) EXAMINATION ${studentData.resultYear}`;

    return (
      <div style={{
        minHeight:'100vh',
        background:'#fff',
        fontFamily:'"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        color:'#222',
        padding:'24px 22px',
        boxSizing:'border-box',
      }}>

        {/* Top header — CISCE logo + exam title */}
        <div style={{ display:'flex', alignItems:'flex-start', gap:'18px', marginBottom:'22px' }}>
          <img
            src={amblumLogo}
            alt="CISCE"
            style={{ height:'82px', width:'auto', objectFit:'contain', flexShrink:0 }}
          />
          <div style={{ flex:1, paddingTop:'4px' }}>
            <div style={{ fontSize:'15px', fontWeight:600, color:'#444', lineHeight:1.35, marginBottom:'12px' }}>
              Council for the Indian School Certificate<br/>
              Examination (CISCE)
            </div>
            <div style={{ fontSize:'15px', fontWeight:600, color:'#222', lineHeight:1.35 }}>
              {examTitle}
            </div>
          </div>
        </div>

        {/* Top divider */}
        <div style={{ height:'1px', background:'#e5e7eb', margin:'0 0 0 0' }} />

        {/* Field rows */}
        <div style={{ fontSize:'15px', color:'#222' }}>
          {[
            ['Index No.',        fmtIndexNo(studentData.indexNo)],
            ['Unique ID',        studentData.candidateId || 'N/A'],
            ["Candidate's Name", (studentData.candidateName || '').toUpperCase()],
            ['School Name',      (studentData.school || '').toUpperCase()],
            ['Year',             studentData.resultYear || 'N/A'],
            ['Result',           (studentData.result || 'N/A').toUpperCase()],
          ].map(([label, value]) => (
            <div
              key={label}
              style={{
                display:'grid',
                gridTemplateColumns:'160px 18px 1fr',
                alignItems:'flex-start',
                padding:'14px 0',
                borderBottom:'1px solid #f0f0f0',
                fontSize:'15px',
              }}
            >
              <span style={{ fontWeight:600, color:'#333' }}>{label}</span>
              <span style={{ color:'#888' }}>:</span>
              <span style={{ fontWeight:600, color:'#222', wordBreak:'break-word' }}>{value}</span>
            </div>
          ))}
        </div>

        {/* Powered by DigiLocker pill */}
        <div className="flex justify-left items-end h-[120px] mb-[10px] ml-[0px]">
          <img
            src={digiLogo}
            alt="POWRED BY DIGILOCKER"
            style={{ height:'100px', width:'auto', objectFit:'contain', flexShrink:0 }}
          />
              </div>

      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  //  FULL CERTIFICATE VIEW — original UI for admin / dashboard navigation
  // ════════════════════════════════════════════════════════════════════════

  // Derived
  const { mother, father } = extractParents(studentData.sonOf);
  const subjects           = (studentData.subjects || []).filter(s => s.subjectName);
  const declDate           = fmtDeclaration(studentData.declarationDate);
  const isXII              = studentData.classSelected === 'Class XII';

  const examLine = isXII
    ? `INDIAN SCHOOL CERTIFICATE (CLASS XII) YEAR ${studentData.resultYear} EXAMINATION`
    : `INDIAN SCHOOL CERTIFICATE (CLASS X) YEAR ${studentData.resultYear} EXAMINATION`;

  const footerCode = isXII ? 'ISC' : 'ICSE';
  const footerYear = `(Y ${studentData.resultYear} R)`;

  const nowStr = (() => {
    const n = new Date();
    return `${String(n.getDate()).padStart(2,'0')}/${String(n.getMonth()+1).padStart(2,'0')}/${n.getFullYear()} ${String(n.getHours()).padStart(2,'0')}:${String(n.getMinutes()).padStart(2,'0')}:${String(n.getSeconds()).padStart(2,'0')}`;
  })();

  // ── Style tokens ──────────────────────────────────────────────────────
  const GREY = '#ededed';

  const PAGE_BORDER  = '0px solid #000';
  const PAGE_SHADOW  = '0 6px 18px rgba(0, 0, 0, 0.10), 0 2px 6px rgba(0, 0, 0, 0.06)';

  const page1OuterStyle = {
    ...BASE,
    fontSize: '11.5px',
    lineHeight: 1.45,
    padding: '24px 28px 130px 28px',
    border: PAGE_BORDER,
    background: '#fff',
    boxShadow: PAGE_SHADOW,
    boxSizing: 'border-box'
  };

  const page1InnerStyle = {
    border: '1px solid #000',
    padding: '10px 14px 28px 14px',
    boxSizing: 'border-box'
  };

  const page2OuterStyle = {
    ...BASE,
    fontSize: '11.5px',
    lineHeight: 1.45,
    padding: '24px 28px 130px 28px',
    border: '0px solid #000',
    background: '#fff',
    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.10), 0 2px 6px rgba(0, 0, 0, 0.06)',
    boxSizing: 'border-box',
    marginTop: '14px',
    pageBreakBefore: 'always',
  };

  const page2InnerStyle = {
    border: '1px solid #000',
    padding: '40px 18px 30px 18px',
    boxSizing: 'border-box',
    minHeight: '1092px',
    display: 'flex',
    flexDirection: 'column',
  };

  const cell        = (extra = {}) => ({ border: '1px solid #000', padding: '3px 8px', fontSize: '11px', ...extra });
  const cellSubject = (extra = {}) => cell({ textAlign: 'left', paddingLeft: '4px', ...extra });
  const cellCenter  = (extra = {}) => cell({ textAlign: 'center', ...extra });

  const NOTES = [
    'This data is accessed using DigiLocker.',
    'This certificate is generated by DigiLocker (https://digilocker.gov.in) directly from concerned Department Database.',
    'This digitally signed document is legally valid as per the IT Act, 2000 when used electronically.',
    'This digitally signed document is valid as per the IT Act.',
    'To verify the print out of this certificate, download DigiLocker Android application from Google Play and scan the QR code on the printed certificate.',
    'The pass mark for each subject is 35%',
    'No divisions are awarded.',
  ];

  return (
    <div style={{
      background: '#e5e7eb',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '32px 0',
    }}>
      <style>{`
  @media print {
    @page { size: A4; margin: 0; }
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: #fff !important;
    }
    body * { visibility: hidden; }
    #result-content, #result-content * { visibility: visible; }
    #result-content {
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      zoom: 1.3;
      box-shadow: none !important;
    }
    #result-content > div {
      box-shadow: none !important;
      margin-top: 0 !important;    /* remove the 14px screen gap in print only */
    }
    #result-content > div:first-child {
      page-break-after: always;
    }
  }
`}</style>

      <div id="result-content" style={{ width: '793px' }}>

        {/* ════════════════════════════════════════════════════
              PAGE 1
            ════════════════════════════════════════════════════ */}
        <div style={page1OuterStyle}>
          <div style={page1InnerStyle}>

            {/* Top corners */}
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', marginBottom:'4px' }}>
              <span>Index No. : <strong>{fmtIndexNo(studentData.indexNo)}</strong></span>
              <span>Certificate No. : <strong>{studentData.certificateNo || 'N/A'}</strong></span>
            </div>

            {/* Logo */}
            <div style={{ width:'100%', textAlign:'center', marginBottom:'6px' }}>
              <img src={cisceLogo} alt="CISCE" style={{ height:'90px', objectFit:'contain', display:'block', margin:'0 auto' }} />
            </div>

            {/* Header */}
            <div style={{ textAlign:'center', lineHeight:1.35, marginBottom:'10px' }}>
              <div style={{ fontSize:'16px', fontWeight:'bold', textTransform:'uppercase' }}>
                COUNCIL FOR THE INDIAN SCHOOL CERTIFICATE EXAMINATIONS
              </div>
              <div style={{ fontSize:'13px', fontWeight:'bold', textTransform:'uppercase', marginTop:'2px' }}>
                {examLine}
              </div>
              <div style={{ fontSize:'12px', fontWeight:'bold', textTransform:'uppercase', marginTop:'1px' }}>
                PASS CERTIFICATE CUM STATEMENT OF MARKS
              </div>
            </div>

            {/* Candidate Details + Photo */}
            <div style={{ display:'flex', alignItems:'flex-start', marginBottom:'8px' }}>
              <div style={{ flex:1, fontSize:'11.5px' }}>
                {[
                  ['Name',        (studentData.candidateName || '').toUpperCase()],
                  ['Unique ID',   studentData.candidateId || ''],
                  ['Gender',      (studentData.gender || '').toUpperCase()],
                  ['Mother',      mother],
                  ['Father',      father],
                  ['School',      (studentData.school || '').toUpperCase()],
                  ...(studentData.classSelected === 'Class X' && studentData.dob ? [['Date of Birth', fmtDob(studentData.dob)]] : []),
                ].map(([lbl, val]) => (
                  <div key={lbl} style={{ display:'flex', marginBottom:'3px' }}>
                    <span style={{ width:'82px', flexShrink:0 }}>{lbl}</span>
                    <span style={{ width:'14px', flexShrink:0 }}>:</span>
                    <strong>{val}</strong>
                  </div>
                ))}
              </div>

              {/* Photo */}
              <div style={{ flexShrink:0, marginLeft:'10px', marginRight:'75px', padding:'5px', border:'1px solid #888', background:'#fff' }}>
                {studentData.photoUrl ? (
                  <img src={studentData.photoUrl} alt="Candidate" crossOrigin="anonymous" style={{ width:'75px', height:'90px', objectFit:'cover', display:'block' }} />
                ) : (
                  <div style={{ width:'75px', height:'90px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'8px', color:'#bbb', textAlign:'center', background:'#fafafa' }}>
                    Photo<br/>Not<br/>Available
                  </div>
                )}
              </div>
            </div>

            {/* Table intro */}
            <p style={{ fontSize:'11px', margin:'0 0 4px 0' }}>
              The subject-wise marks and grades attained by the candidate are given below:
            </p>

            {/* Marks Table */}
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'11px', marginBottom:'6px' }}>
              <tbody>
                <tr style={{ background: GREY }}>
                  <td style={cellSubject({ width:'42%' })}>Subjects</td>
                  <td style={cellCenter({ width:'38%' })}>Percentage Marks</td>
                  <td style={cellCenter({ width:'20%' })}>Grade</td>
                </tr>
                {subjects.map((sub, i) => (
                  <tr key={i}>
                    <td style={cellSubject()}>{sub.subjectName.toUpperCase()}</td>
                    <td style={cellCenter()}>{fmtMarks(sub.marks)}</td>
                    <td style={cellCenter()}>{fmtGrade(sub.grade)}</td>
                  </tr>
                ))}
                <tr style={{ background: GREY }}>
                  <td colSpan={3} style={cellSubject({ fontWeight:'bold' })}>Internal Assessment</td>
                </tr>
                {studentData.supwGrade && (
                  <tr>
                    <td colSpan={2} style={cellSubject()}>SUPW AND COMMUNITY SERVICE</td>
                    <td style={cellCenter()}>{studentData.supwGrade.toUpperCase()}</td>
                  </tr>
                )}
                <tr style={{ background: GREY }}>
                  <td style={cellSubject({ fontWeight:'bold' })}>Result</td>
                  <td style={cellCenter()}></td>
                  <td style={cellCenter({ fontWeight: 'bold' })}>Qualified</td>
                </tr>
              </tbody>
            </table>

            {/* Date of declaration */}
            {declDate && (
              <p style={{ fontSize:'11px', margin:'4px 0 8px 0' }}>
                Date of declaration of Result : <strong>{declDate}</strong>
              </p>
            )}

            {/* Signatures */}
            <div style={{ display:'flex', justifyContent:'space-around', alignItems:'flex-end', margin:'2px 30px 0 30px' }}>
              <div style={{ textAlign:'center' }}>
                <img src={chiefExecSig} alt="Chief Executive Signature" style={{ height:'24px', objectFit:'contain', display:'block', margin:'0 auto 2px auto' }} />
                <div style={{ fontWeight:'bold', fontSize:'10.5px' }}>Chief Executive &amp; Secretary</div>
                <div style={{ fontSize:'10px' }}>Council for the Indian School Certificate Examinations</div>
              </div>
              <div style={{ textAlign:'center' }}>
                <img src={chairmanSig} alt="Chairman Signature" style={{ height:'24px', objectFit:'contain', display:'block', margin:'0 auto 2px auto' }} />
                <div style={{ fontWeight:'bold', fontSize:'10.5px' }}>Chairman</div>
                <div style={{ fontSize:'10px' }}>Council for the Indian School Certificate Examinations</div>
              </div>
            </div>

            <div style={{ height:'10px' }} />

            {/* QR | DigiLocker | Sig box */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
              <div style={{ flexShrink:0 }}>
                {/* QR opens the live DigiLocker domain at root with ?rollid= */}
                <QRCodeSVG
                  value={`http://digilocker.cisceu.org/?rollid=${encodeURIComponent(studentData.indexNo)}`}
                  size={88}
                  level="H"
                />
              </div>
              <div className="flex justify-center items-end h-[110px] mb-[20px] ml-[20px]">
                <img
                  src="https://digilocker.cisceboard.org/assets/nad_verified.png"
                  alt="Verification Seal"
                  className="max-h-[80px] object-contain w-auto"
                />
              </div>
              <div style={{ border:'1px solid #666', padding:'7px 10px', width:'200px', fontSize:'10px', lineHeight:1.6, flexShrink:0 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'5px' }}>
                  <span>Digitally signed on</span>
                  <div className="flex items-center mb-0.5">
                    <img
                      src="https://digilocker.cisceboard.org/assets/verified.png"
                      alt="Verified Seal"
                      className="object-contain max-h-[35px]"
                    />
                  </div>
                </div>
                <div>Date : {nowStr} IST</div>
              </div>
            </div>

            {/* Notes */}
            <div style={{ border:'1px solid #000', padding:'5px 10px', fontSize:'10px', lineHeight:1.55 }}>
              <div style={{ fontWeight:'bold', marginBottom:'3px' }}>Note:</div>
              {NOTES.map((text, i) => (
                <div key={i} style={{ display:'flex', alignItems:'flex-start', marginBottom:'1px' }}>
                  <span style={{ width:'18px', flexShrink:0 }}>{i + 1}.</span>
                  <span style={{ flex:1 }}>{text}</span>
                </div>
              ))}
            </div>

          </div>
        </div>
        {/* END PAGE 1 */}


        {/* ════════════════════════════════════════════════════
              PAGE 2
            ════════════════════════════════════════════════════ */}
        <div style={page2OuterStyle}>
          <div style={page2InnerStyle}>

            <div style={{ width: '85%', margin: '0 auto' }}>

              <div style={{ textAlign:'center', fontWeight:'bold', fontSize:'13px', marginBottom:'14px', letterSpacing:'0.04em' }}>
                THE EXAMINATION
              </div>

              <div style={{ fontWeight:'bold', fontSize:'11px', textAlign:'center', marginBottom:'24px', lineHeight:1.55 }}>
                {PAGE2_EXAM_DESCRIPTION}
              </div>

              <div style={{ textAlign:'center', fontWeight:'bold', fontSize:'13px', marginBottom:'18px', letterSpacing:'0.04em' }}>
                INTERPRETATION OF GRADES
              </div>

              <div style={{ fontWeight:'bold', fontSize:'11.5px', marginBottom:'7px' }}>
                I.&nbsp; EXTERNAL EXAMINATION :
              </div>
              <div style={{ fontSize:'11px', textAlign:'left', marginBottom:'12px', lineHeight:1.55 }}>
                Attainment in a subject is indicated on this document by percentage marks and a grade, of which<br/>
                Grade 1 is the highest and Grade 9 the lowest.
              </div>
              <div style={{ textAlign:'center', fontSize:'11px', marginBottom:'6px' }}>
                The interpretation of the grades is as follows:
              </div>

              <table style={{ margin:'0 auto 16px auto', borderCollapse:'collapse', fontSize:'11px' }}>
                <thead>
                  <tr>
                    <th style={{ padding:'2px 30px', textAlign:'center', fontWeight:'bold' }}>Grade</th>
                    <th style={{ padding:'2px 30px', textAlign:'center', fontWeight:'bold' }}>Standard</th>
                  </tr>
                </thead>
                <tbody>
                  {[['1, 2','Very Good'],['3, 4, 5, 6','Credit'],['7, 8','Pass'],['9','Fail']].map(([g,s]) => (
                    <tr key={g}>
                      <td style={{ padding:'1px 30px', textAlign:'center' }}>{g}</td>
                      <td style={{ padding:'1px 30px', textAlign:'center' }}>{s}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ border:'1px solid #000', padding:'7px 14px', marginBottom:'18px', fontSize:'11px', lineHeight:1.5, textAlign: 'center' }}>
                English is a Compulsory Subject and all other subjects offered by the candidates taking the<br/>
                Indian School Certificate (ISC) Examination are Elective Subjects.
              </div>

              <div style={{ fontWeight:'bold', fontSize:'11.5px', marginBottom:'7px' }}>
                II.&nbsp; INTERNAL ASSESSMENT :
              </div>
              <div style={{ fontSize:'11px', textAlign:'left', marginBottom:'12px', lineHeight:1.55 }}>
                The standard attained in Socially Useful Productive Work &amp; Community Service, abbreviated on<br/>
                the face of this document as SUPW &amp; COMMUNITY SERVICE, is indicated by a letter grade, of<br/>
                which Grade A is the highest and Grade E the lowest.
              </div>
              <div style={{ textAlign:'center', fontSize:'11px', marginBottom:'6px' }}>
                The interpretation of the grades is as follows:
              </div>

              <table style={{ margin:'0 auto 20px auto', borderCollapse:'collapse', fontSize:'11px' }}>
                <thead>
                  <tr>
                    <th style={{ padding:'2px 30px', textAlign:'center', fontWeight:'bold' }}>Grade</th>
                    <th style={{ padding:'2px 30px', textAlign:'center', fontWeight:'bold' }}>Standard</th>
                  </tr>
                </thead>
                <tbody>
                  {[['A','Very Good'],['B','Good'],['C','Satisfactory'],['D','Fair'],['E','Fail']].map(([g,s]) => (
                    <tr key={g}>
                      <td style={{ padding:'1px 30px', textAlign:'center' }}>{g}</td>
                      <td style={{ padding:'1px 30px', textAlign:'center' }}>{s}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ border:'1px solid #000', padding:'10px 16px', fontSize:'11px', lineHeight:1.6 }}>
                <div style={{ fontWeight:'bold', textAlign:'center', marginBottom:'8px', fontSize:'11.5px' }}>
                  Awards and Conditions for Awards:
                </div>
                <p style={{ margin:'0 0 8px 0', textAlign:'left' }}>
                  <strong>Pass Certificate Cum Statement of Marks</strong> will be awarded to candidates who attain the pass<br/>
                  standard in four or more subjects which must include the subject English.
                </p>
                <p style={{ margin:'0 0 8px 0', textAlign:'left' }}>
                  Provided that no candidate, except as otherwise exempted by CISCE, shall be awarded a Pass<br/>
                  Certificate Cum Statement of Marks unless in addition to fulfilling the conditions above, the<br/>
                  candidate has attained a pass grade (A&nbsp;-&nbsp;D) in SUPW and COMMUNITY SERVICE as<br/>
                  examined/assessed internally by the school.
                </p>
                <p style={{ margin:'0 0 8px 0', textAlign:'left' }}>
                  <strong>Statement of Marks</strong> will be issued to the candidates whose Result is declared as Not<br/>
                  Qualified.
                </p>
                <p style={{ margin:'0', textAlign:'left' }}>
                  <strong>Supplementary Statement of Marks</strong> will be issued to candidates who appear in a subsequent<br/>
                  examination in one or more subjects.
                </p>
              </div>

            </div>

            {/* Footer */}
            <div style={{
              marginTop: 'auto',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              position: 'relative',
              paddingTop: '24px',
            }}>
              <div style={{ textAlign:'center', fontWeight:'bold', fontSize:'13px', lineHeight:1.3 }}>
                <div>{footerCode}</div>
                <div>{footerYear}</div>
              </div>
              <img src={cisceFLogo} alt="CISCE" style={{ height:'48px', objectFit:'contain', position:'absolute', right:'10px', bottom:0 }} />
            </div>

          </div>
        </div>
        {/* END PAGE 2 */}

      </div>
    </div>
  );
};

export default DigiLockerResultPage;